import { createClient } from '@supabase/supabase-js'
import { isLabsMode, isWriteRpc, sandboxBuilder } from './labs'

// ── Session storage: localStorage-primary, cookie-mirror ─────────────────────
// WHY: a Supabase session blob (two JWTs + user object) is commonly 3–5 KB. The
// old cookie-only storage silently dropped any single cookie over ~4 KB, so the
// installed iOS PWA frequently cold-started logged-OUT and forced a fresh magic
// link every launch. localStorage has no size cap and persists indefinitely in
// an installed PWA (exempt from Safari's 7-day script-storage eviction), so it is
// the reliable home for the session.

const cookieGet = (key) => {
  const match = document.cookie.split('; ').find(row => row.startsWith(`${key}=`))
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null
}
const cookieRemove = (key) => {
  document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; Secure`
}

// UPDATE (15.100): the cookie is now a ONE-SHOT migration, never a live mirror.
// WHY: when Safari and the installed PWA both adopt the same session via the
// cookie, they share ONE refresh-token family. Supabase rotates refresh tokens
// on every refresh — whichever context refreshes second presents an already-
// used token, and reuse detection revokes the WHOLE family → both contexts get
// logged out. That was the recurring "magic link again" loop. Each context must
// own an independent session: read any legacy cookie once, promote it, delete
// it, and never write the session back to a cookie.
const hybridStorage = {
  getItem(key) {
    try {
      const ls = window.localStorage.getItem(key)
      if (ls != null) return ls
    } catch {}
    // One-time migration of a legacy cookie-stored session, then burn the
    // cookie so no second context can adopt the same refresh-token family.
    const ck = cookieGet(key)
    if (ck != null) {
      try { window.localStorage.setItem(key, ck) } catch {}
      cookieRemove(key)
    }
    return ck
  },
  setItem(key, value) {
    try { window.localStorage.setItem(key, value) } catch {}
    // No cookie mirror (see header note) — and clear any legacy one.
    cookieRemove(key)
  },
  removeItem(key) {
    try { window.localStorage.removeItem(key) } catch {}
    cookieRemove(key)
  },
}

// Dev-only swallowed-error sweep (2026-10-11): supabase-js never throws on a
// PostgREST/RPC error, it resolves { error } — and hundreds of call sites read
// only { data }. In dev, log every non-2xx API response with method, path and
// the server's message, so a walk through the app surfaces what fails silently.
// Zero cost in production: the option is simply not passed.
const devFetch = import.meta.env.DEV ? async (input, init) => {
  const res = await fetch(input, init)
  if (res.status >= 400 && res.status !== 406) {   // 406 = .single() found no row, routine
    try {
      const url = typeof input === 'string' ? input : input.url
      const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0]
      const body = await res.clone().text()
      console.warn(`[supabase ${res.status}] ${(init && init.method) || 'GET'} ${path} — ${body.slice(0, 160)}`)
    } catch {}
  }
  return res
} : undefined

const client = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    ...(devFetch ? { global: { fetch: devFetch } } : {}),
    auth: {
      storage:           hybridStorage,
      persistSession:    true,
      autoRefreshToken:  true,
      detectSessionInUrl: true,   // picks up access_token hash from magic-link redirect
    },
  }
)

// ── Sandbox gate (lib/labs.js) ───────────────────────────────────────────────
// While Sandbox mode is on, every write-shaped call on this client short-circuits
// to a resolved `{ data: null, error: null }` and nothing else changes: reads,
// auth, realtime and every method not named below are the real client, bound.
// Off (the default for everyone but an admin who flipped it), `from`/`rpc`/
// `storage` are the real ones — the flag is a synchronous localStorage read, so
// the check costs nothing on the hot path.
const TABLE_WRITES = new Set(['insert', 'update', 'upsert', 'delete'])
const STORAGE_WRITES = new Set(['upload', 'update', 'remove', 'move', 'copy', 'createSignedUploadUrl', 'uploadToSignedUrl'])
const bound = (obj, prop) => { const v = obj[prop]; return typeof v === 'function' ? v.bind(obj) : v }

function gateTable(builder, table) {
  return new Proxy(builder, {
    get(b, prop) {
      if (TABLE_WRITES.has(prop)) return () => sandboxBuilder(`${prop} ${table}`)
      return bound(b, prop)
    },
  })
}
function gateBucket(bucket, name) {
  return new Proxy(bucket, {
    get(b, prop) {
      if (STORAGE_WRITES.has(prop)) return async (path) => { console.info(`[Sandbox] skipped storage ${prop}: ${name}/${path}`); return { data: { path }, error: null } }
      return bound(b, prop)
    },
  })
}

export const supabase = new Proxy(client, {
  get(c, prop) {
    if (prop === 'from') return (table) => { const b = c.from(table); return isLabsMode() ? gateTable(b, table) : b }
    if (prop === 'rpc') return (fn, ...rest) => (isLabsMode() && isWriteRpc(fn)) ? sandboxBuilder(`rpc ${fn}`) : c.rpc(fn, ...rest)
    if (prop === 'storage') {
      const st = c.storage
      return new Proxy(st, { get(s, p) { if (p === 'from') return (name) => { const b = s.from(name); return isLabsMode() ? gateBucket(b, name) : b }; return bound(s, p) } })
    }
    return bound(c, prop)
  },
})
